import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import APP_BASE_URL from "../../components/Api/globalApi";
import moment from "moment";
import { FaArrowLeft, FaPaperPlane } from "react-icons/fa";
import io from "socket.io-client";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./VirtualMallChat.css";

export default function VirtualMallChatView({ chat, onBack }) {
  const { c_id, c_to, image, u_first_name } = chat;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const socket = useRef(null);

  const Userid =
    localStorage.getItem("user_id") ||
    localStorage.getItem("virtual_user_id");

  // Scroll to bottom
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Setup socket
  useEffect(() => {
    if (!c_id) return;

    socket.current = io("https://api.springwayhub.com/", {
      transports: ["websocket"],
    });

    socket.current.on("connect", () => {
      console.log("Socket connected:", socket.current.id);
    });

    socket.current.on("connect_error", (err) => {
      console.error("Socket error:", err);
    });

    // Join room
    socket.current.emit("c_id", c_id, (response) => {
      console.log("Room join:", response);
    });

    // Listen for incoming messages in this room
    // Skip own messages — already added optimistically on send
    socket.current.on(c_id, (message) => {
      console.log("New socket message:", message);
      if (message.result && String(message.user_id) !== String(Userid)) {
        // Only add admin/other-user messages, not our own echo
        setMessages((prev) => [
          ...prev,
          {
            m_id: Date.now(),
            m_message: message.message,
            m_from_id: message.user_id ? parseInt(message.user_id) : parseInt(c_to),
            m_to_id: message.recipient,
            m_date: message.date || moment().format("YYYY-MM-DD"),
            m_time: message.time || moment().format("HH:mm:ss"),
            m_status: "sent",
          },
        ]);
      }
    });

    // Fetch existing messages
    fetchMessages();

    return () => {
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [c_id]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${APP_BASE_URL}/message/list`,
        { c_id, page_no: "", limit: "" },
        { headers: { user_id: Userid } }
      );
      if (response.data.result === true) {
        setMessages(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (!newMessage.trim() || !socket.current) return;

    const msgText = newMessage.trim();
    setNewMessage("");

    // Add message optimistically immediately — don't wait for socket callback
    // (backend uses io.emit broadcast, not acknowledgment)
    setMessages((prev) => [
      ...prev,
      {
        m_id: `temp_${Date.now()}`,
        m_message: msgText,
        m_from_id: parseInt(Userid),
        m_to_id: 1,
        m_date: moment().format("YYYY-MM-DD"),
        m_time: moment().format("HH:mm:ss"),
        m_status: "sent",
      },
    ]);

    // Emit without callback — backend broadcasts via io.emit, not socket.emit ack
    socket.current.emit("sent", {
      message: msgText,
      recipient: 1, // Admin is always user_id 1
      c_id: c_id,
      user_id: Userid,
    });

    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = moment(msg.m_date).format("YYYY-MM-DD");
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  const formatDateLabel = (dateStr) => {
    const date = moment(dateStr);
    if (date.isSame(moment(), "day")) return "Today";
    if (date.isSame(moment().subtract(1, "days"), "day")) return "Yesterday";
    return date.format("D MMM YYYY");
  };

  return (
    <div className="vm-chatview-wrapper">
      {/* Chat Header */}
      <div className="vm-chatview-header">
        {onBack && (
          <button className="vm-back-btn" onClick={onBack}>
            <FaArrowLeft size={18} />
          </button>
        )}
        <div className="vm-chatview-avatar">
          {image ? (
            <img src={image} alt={u_first_name} />
          ) : (
            <div className="vm-avatar-placeholder vm-avatar-md">
              {(u_first_name || "A").charAt(0).toUpperCase()}
            </div>
          )}
          <span className="vm-online-dot" />
        </div>
        <div className="vm-chatview-info">
          <h3>{u_first_name || "Admin"}</h3>
          <span className="vm-online-status">Online</span>
        </div>
      </div>

      {/* Messages Area */}
      <div className="vm-chatview-body">
        {loading ? (
          <div className="vm-chat-loading">
            <div className="vm-spinner" />
            <p>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="vm-chat-empty-msg">
            <div className="vm-chat-empty-icon">👋</div>
            <p>Say hello to {u_first_name || "Admin"}!</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date}>
              {/* Date separator */}
              <div className="vm-date-separator">
                <span>{formatDateLabel(date)}</span>
              </div>
              {msgs.map((msg, i) => {
                const isSender = msg.m_from_id === parseInt(Userid);
                return (
                  <div
                    key={msg.m_id || i}
                    className={`vm-message-row ${isSender ? "vm-sender" : "vm-receiver"}`}
                  >
                    <div className={`vm-bubble ${isSender ? "vm-bubble-sent" : "vm-bubble-received"}`}>
                      <p className="vm-bubble-text">{msg.m_message}</p>
                      <div className="vm-bubble-meta">
                        <span className="vm-bubble-time">
                          {moment(msg.m_time, "HH:mm:ss").format("hh:mm A")}
                        </span>
                        {isSender && (
                          <span className="vm-tick">
                            {msg.m_status === "read" ? (
                              <i className="fas fa-check-double" style={{ color: "#53bdeb" }} />
                            ) : (
                              <i className="fas fa-check" style={{ color: "rgba(255,255,255,0.7)" }} />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className="vm-chatview-footer">
        <div className="vm-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="vm-message-input"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            autoFocus
          />
          <button
            className={`vm-send-btn ${newMessage.trim() ? "vm-send-active" : ""}`}
            onClick={handleSend}
            disabled={!newMessage.trim()}
          >
            <FaPaperPlane size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
